/**
 * Root app component.
 *
 * Auth + path-based portal routing — no external router.
 * Uses window.location.pathname + history.replaceState only.
 *
 * /admin  → operator portal (role='operator' enforced)
 * /login  → client portal (any role)
 * other   → replaceState('/login') then render client portal
 *
 * Authenticated user on /admin or /login → replaceState('/') → AppShell.
 * Logout redirects to /admin (if operator) or /login (if client).
 */

import React from 'react';
import { useAuth } from './hooks/useAuth.js';
import { LoginPage } from './pages/LoginPage.js';
import { AppShell } from './pages/AppShell.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function currentPath() {
  return window.location.pathname;
}

// ── Loading spinner (shared) ──────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="flex flex-col items-center gap-3">
        <div
          className="relative w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #8F6BFF 0%, #4A63E8 100%)' }}
        >
          <span className="text-white text-sm">✦</span>
          <div
            className="absolute inset-[-4px] rounded-full border-2"
            style={{
              borderColor: '#159662',
              animation: 'pulse-ring 1.6s ease-out infinite',
            }}
          />
        </div>
        <p className="text-[12px] text-muted" style={{ fontFamily: '"DM Mono", monospace' }}>
          loading…
        </p>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // ── Authenticated ─────────────────────────────────────────────────────────

  if (user) {
    // Authenticated user on a login portal → redirect to app root
    const path = currentPath();
    if (path === '/admin' || path === '/login') {
      history.replaceState(null, '', '/');
    }

    const handleLogout = async () => {
      // Capture target path before any async operation — role is known here.
      const targetPath = user.role === 'operator' ? '/admin' : '/login';
      try {
        await logout(); // useAuth.logout clears local state in finally, always
      } catch {
        // apiLogout network failure — local state already cleared by useAuth.
        // Fall through to replaceState so the UI reaches the login page.
      }
      // Only mutate URL after logout (and its state cleanup) completes.
      history.replaceState(null, '', targetPath);
    };

    return <AppShell user={user} onLogout={handleLogout} />;
  }

  // ── Unauthenticated ───────────────────────────────────────────────────────

  const path = currentPath();

  if (path === '/admin') {
    // Operator portal — role-enforced login
    const handleAdminLogin = async (email: string, password: string) => {
      await login(email, password, 'operator');
    };

    return <LoginPage variant="operator" onLogin={handleAdminLogin} />;
  }

  // Any other path (including /login) → canonical /login
  if (path !== '/login') {
    history.replaceState(null, '', '/login');
  }

  return <LoginPage variant="client" onLogin={login} />;
}
